import { CoverTemplate, CoverTemplateValues } from '../types';
import { getDefaultCoverValues, buildDocumentFromCoverSections } from './templateUtils';
import { DocumentSection, TextBlock, ImageBlock } from '../../core/ast/document.types';

export const minimalAcademicTemplate: CoverTemplate = {
  id: 'tpl-minimal-academic',
  name: 'Minimalist Academic',
  category: 'general_academic',
  description: 'Clean typographic layout with generous whitespace, subtle institutional branding, and minimal visual distraction.',
  thumbnailBadge: 'Minimal',
  version: 3,
  supportedFields: [
    'universityName', 'universityAddress', 'departmentName', 'logoAssetId', 'showLogo',
    'title', 'subtitle', 'courseCode', 'courseTitle',
    'studentName', 'studentId', 'facultyName', 'submissionDate',
  ],
  defaultValues: getDefaultCoverValues({
    documentTypeLabel: 'Academic Report',
    title: '',
    subtitle: '',
    courseCode: '',
    courseTitle: '',
    studentName: '',
    studentId: '',
    facultyName: '',
    submissionDate: '',
    borderStyle: 'none',
  }),
  compileToDocument: (values: CoverTemplateValues, pageSettings) => {
    const now = new Date().toISOString();
    const blocks: Array<TextBlock | ImageBlock> = [];

    // 1. Center Logo
    if (values.showLogo && values.logoAssetId) {
      blocks.push({
        id: 'blk-cover-logo',
        type: 'image',
        createdAt: now,
        data: {
          assetId: values.logoAssetId,
          previewDataUrl: values.logoAssetId,
          widthPercent: 24,
          alignment: 'center',
          altText: values.universityName || 'East West University',
        },
      });
    }

    // 2. University Name (Center Aligned)
    blocks.push({
      id: 'blk-cover-univ',
      type: 'text',
      createdAt: now,
      data: {
        text: (values.universityName || 'EAST WEST UNIVERSITY').toUpperCase(),
        formatting: { bold: true, fontSizePt: 15, alignment: 'center', color: '#111827' },
      },
    });

    // 3. Address (Center Aligned)
    blocks.push({
      id: 'blk-cover-addr',
      type: 'text',
      createdAt: now,
      data: {
        text: values.universityAddress || 'A/2 Jahurul Islam Ave, Dhaka 1212',
        formatting: { fontSizePt: 9.5, alignment: 'center', color: '#6b7280' },
      },
    });

    // Spacing
    blocks.push({
      id: 'blk-cover-sp1',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 14 } },
    });

    // 4. Large Minimalist Title
    if (values.title) {
      blocks.push({
        id: 'blk-cover-title',
        type: 'text',
        createdAt: now,
        data: {
          text: values.title,
          formatting: { bold: true, fontSizePt: 22, alignment: 'center', color: '#111827' },
        },
      });
    }

    if (values.subtitle) {
      blocks.push({
        id: 'blk-cover-sub',
        type: 'text',
        createdAt: now,
        data: {
          text: values.subtitle,
          formatting: { fontSizePt: 12, alignment: 'center', color: '#4b5563' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp2',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 12 } },
    });

    // 5. Course
    const courseLine = [values.courseCode, values.courseTitle].filter(Boolean).join(': ');
    if (courseLine) {
      blocks.push({
        id: 'blk-cover-course',
        type: 'text',
        createdAt: now,
        data: {
          text: courseLine,
          formatting: { bold: true, fontSizePt: 11, alignment: 'center', color: '#1f2937' },
        },
      });
    }

    // Spacing
    blocks.push({
      id: 'blk-cover-sp3',
      type: 'text',
      createdAt: now,
      data: { text: '\n\n', formatting: { fontSizePt: 12 } },
    });

    // 6. Student & Submission Metadata
    const authorLine = values.studentName ? `Author: ${values.studentName}${values.studentId ? ` (${values.studentId})` : ''}` : '';
    const instructorLine = values.facultyName ? `Instructor: ${values.facultyName}` : '';
    const dateLine = values.submissionDate ? `Date: ${values.submissionDate}` : '';

    const metaText = [authorLine, instructorLine, dateLine].filter(Boolean).join('\n');
    if (metaText) {
      blocks.push({
        id: 'blk-cover-meta',
        type: 'text',
        createdAt: now,
        data: {
          text: metaText,
          formatting: { fontSizePt: 11, alignment: 'center', color: '#374151' },
        },
      });
    }

    const coverSection: DocumentSection = {
      id: 'sec-cover-minimal',
      title: 'Cover Page',
      type: 'cover',
      enabled: true,
      collapsed: false,
      blocks,
    };

    return buildDocumentFromCoverSections(values, [coverSection], pageSettings);
  },
};
